import { useEffect, useState } from 'react';

interface UseIframeContentReturn {
  content: string | undefined;
  useSrcAttribute: boolean;
}

/**
 * Hook to parse HTML content and determine whether to use iframe src or srcDoc attribute.
 *
 * For standalone iframes with a valid src attribute, extracts and returns the src URL.
 * For all other HTML content (including nested iframes or content without src),
 * returns the original HTML to be rendered with srcDoc.
 *
 * @param htmlContent - The HTML string to parse
 * @returns An object containing the parsed content and a flag indicating whether to use src attribute
 */
export const useIframeContent = (
  htmlContent: string | undefined,
): UseIframeContentReturn => {
  const [content, setContent] = useState<string | undefined>(undefined);
  const [useSrcAttribute, setUseSrcAttribute] = useState(false);

  useEffect(() => {
    const parseHtml = (htmlString: string) => {
      try {
        // Trim the content to remove whitespace
        const trimmedHtml = htmlString.trim();

        // Check if the content is exactly one iframe (no other content)
        // This regex checks if the string starts with an iframe tag and ends with an iframe closing tag
        const standaloneIframeRegex = /^\s*<iframe[^>]*>.*<\/iframe>\s*$/s;

        if (standaloneIframeRegex.test(trimmedHtml)) {
          // Extract src attribute using regex
          const srcMatch = trimmedHtml.match(/src=["']([^"']*)["']/);
          if (srcMatch && srcMatch[1]) {
            // Only use src if this is a standalone iframe with a valid src
            setContent(srcMatch[1]);
            setUseSrcAttribute(true);
            return;
          }
        }

        // If it's not a standalone iframe or has no src, or contains nested iframes,
        // use the original HTML with srcDoc to render everything as-is
        setContent(htmlString);
        setUseSrcAttribute(false);
      } catch (error) {
        console.error('Error parsing HTML:', error);
        // Fall back to using the original HTML
        setContent(htmlString);
        setUseSrcAttribute(false);
      }
    };

    if (htmlContent) {
      parseHtml(htmlContent);
    }
  }, [htmlContent]);

  return { content, useSrcAttribute };
};
